import { z } from "zod";

export const CompassSavedStationSchema = z.object({
  id: z.string().regex(/^[a-f0-9]{64}$/),
  providerId: z.string().min(1).max(100),
  stationId: z.string().min(1).max(512),
  name: z.string().min(1).max(512),
  address: z.string().max(2048),
  longitude: z.number().min(-180).max(180),
  latitude: z.number().min(-90).max(90),
  favorite: z.boolean(),
  priceReminder: z
    .object({
      enabled: z.boolean(),
      maximumPrice: z.number().finite().positive().max(100),
      startMinute: z.number().int().min(0).max(1439),
      endMinute: z.number().int().min(0).max(1440),
      chargingType: z.enum(["any", "ac", "dc"]),
    })
    .nullable(),
  timeReminder: z
    .object({
      enabled: z.boolean(),
      minute: z.number().int().min(0).max(1439),
      leadMinutes: z.number().int().min(0).max(1440),
    })
    .nullable(),
  updatedAt: z.string().datetime(),
});
export type CompassSavedStation = z.infer<typeof CompassSavedStationSchema>;

export const CompassFeedbackFields = [
  "address",
  "opening",
  "equipment",
  "price",
  "fault",
  "environment",
] as const;
export const COMPASS_FEEDBACK_RECORD_LIMIT = 100;
export const CompassStationFeedbackSchema = z
  .object({
    id: z.string().uuid(),
    providerId: z.string().min(1).max(100),
    stationId: z.string().min(1).max(512),
    stationName: z.string().max(512),
    field: z.enum(CompassFeedbackFields),
    observed: z.string().min(1).max(2000),
    expected: z.string().max(1000),
    baseline: z.string().max(32_768),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    state: z.enum(["open", "reviewed", "resolved"]),
    checkedAt: z.string().datetime().nullable(),
    sourceValue: z.string().max(32_768).nullable(),
    resolution: z.string().max(1000),
  })
  .strict()
  .refine(
    (value) =>
      value.state !== "resolved" ||
      Boolean(value.checkedAt && value.resolution.trim()),
    {
      message:
        "A resolved feedback record requires a source review and resolution evidence",
    },
  );
export type CompassStationFeedback = z.infer<
  typeof CompassStationFeedbackSchema
>;

export const CompassReminderStatusSchema = z.object({
  entityId: z.string(),
  status: z.enum([
    "ready",
    "credentialRequired",
    "unsupported",
    "unavailable",
    "failed",
  ]),
  checkedAt: z.string().datetime(),
  nextCheckAt: z.string().datetime(),
});
export const CompassPriceAlertSchema = z.object({
  id: z.string(),
  entityId: z.string(),
  stationName: z.string(),
  providerId: z.string(),
  price: z.number().finite().nonnegative(),
  startsAt: z.string().datetime(),
  endsAt: z.string().datetime(),
  observedAt: z.string().datetime(),
});
export const CompassReminderFeedSchema = z.object({
  statuses: z.array(CompassReminderStatusSchema),
  alerts: z.array(CompassPriceAlertSchema),
});
export type CompassReminderFeed = z.infer<typeof CompassReminderFeedSchema>;
